// src/features/ad-form/utils/changesFun.js
import { getAdContent } from "../../../api/ai/getAdContent";
import { getCategories } from "../../../api/getCategories";

export function changesFun(setAnnouncementName, setVideoLink, setDescription, setSelectedCategory, setSelectedSubcategory, setImages, setSelectedLocation, setCustomTags, setVolumeUnit, setTagsArray, setAISuggestions) {
    const saveToLocalStorage = (key, value) => {
        localStorage.setItem(key, JSON.stringify(value));
    };

    let aiTimeout = null;
    let isProcessing = false;

    const handleAnnouncementInputChange = (value) => {
        setAnnouncementName(value);
    };
    const handleVideoLinkChange = (value) => setVideoLink(value);
    const handleDescriptionChange = (value) => setDescription(value);

    const handleSelectCategory = ({ categoryId, subcategoryId }) => {
        setSelectedCategory(categoryId.id);
        setSelectedSubcategory(subcategoryId.id);
        saveToLocalStorage("selectedCategory", categoryId.id);
        saveToLocalStorage("selectedSubcategory", subcategoryId.id);
    };
    const handleImagesChange = (imageFiles) => setImages(imageFiles);

    const handleSelectLocation = (location) => {
        setSelectedLocation(location);
        saveToLocalStorage("selectedLocation", location);
    };

    const handleCustomTagsChange = (e) => {
        const value = e.target.value;
        setCustomTags(value);
        saveToLocalStorage("customTags", value);
    };

    const handleVolumeChange = (selectedOption) => {
        // selectedOption is now the key (e.g., "piece")
        localStorage.setItem("volumeUnit", selectedOption);
        setVolumeUnit(selectedOption); // Set the state to the key directly
    };

    const fetchAISuggestions = async (title) => {
        try {
            const currentLang = localStorage.getItem('language') || 'ru';
            const aiResponse = await getAdContent(title, currentLang);
            
            console.log('AI Suggestions:', aiResponse); // Debug log
            
            // Set suggestions for display
            setAISuggestions(aiResponse);
        } catch (error) {
            console.error("AI suggestions failed:", error);
            setAISuggestions(null);
        }
    };

    const handleSuggestionClick = async (type, value, aiSuggestions = null) => {
        if (type === 'description') {
            setDescription(value);
            localStorage.setItem("description", value);
        } else if (type === 'tag') {
            const currentTags = JSON.parse(localStorage.getItem("tagsArray") || "[]");
            if (!currentTags.includes(value)) {
                const newTags = [...currentTags, value];
                setTagsArray(newTags);
                localStorage.setItem("tagsArray", JSON.stringify(newTags));
            }
        } else if (type === 'category') {
            // Auto-fill all fields when category is clicked
            if (aiSuggestions) {
                // Fill keyword (announcement name) if available
                if (aiSuggestions.keyword) {
                    setAnnouncementName(aiSuggestions.keyword);
                    localStorage.setItem("announcementName", aiSuggestions.keyword);
                }
                
                // Fill description if available
                if (aiSuggestions.description) {
                    setDescription(aiSuggestions.description);
                    localStorage.setItem("description", aiSuggestions.description);
                }
                
                // Fill tags if available
                if (aiSuggestions.tags && aiSuggestions.tags.length > 0) {
                    const currentTags = JSON.parse(localStorage.getItem("tagsArray") || "[]");
                    const newTags = [...currentTags];
                    aiSuggestions.tags.forEach(tag => {
                        if (!newTags.includes(tag)) {
                            newTags.push(tag);
                        }
                    });
                    setTagsArray(newTags);
                    localStorage.setItem("tagsArray", JSON.stringify(newTags));
                }
            }
            
            // Handle category selection using category_id and subcategory_id from AI response
            if (aiSuggestions && aiSuggestions.category_id && aiSuggestions.subcategory_id) {
                try {
                    const categories = await getCategories();
                    // Find category by ID
                    const foundCategory = categories.find(cat => cat.id === aiSuggestions.category_id);
                    
                    if (foundCategory) {
                        // Find subcategory by ID
                        const foundSubcategory = foundCategory.subcategories?.find(
                            sub => sub.id === aiSuggestions.subcategory_id
                        );
                        
                        if (foundSubcategory) {
                            setSelectedCategory(foundCategory.id);
                            setSelectedSubcategory(foundSubcategory.id);
                            saveToLocalStorage("selectedCategory", foundCategory.id);
                            saveToLocalStorage("selectedSubcategory", foundSubcategory.id);
                            
                            // Save category and subcategory names for display
                            const currentLang = localStorage.getItem('language') || 'ru';
                            
                            const getCategoryName = (cat, lang) => {
                                switch (lang) {
                                    case 'en': return cat.eng_name;
                                    case 'kz': return cat.kz_name;
                                    case 'cn': return cat.ch_name;
                                    default: return cat.name;
                                }
                            };
                            
                            const getSubcategoryName = (sub, lang) => {
                                switch (lang) {
                                    case 'en': return sub.eng_name;
                                    case 'kz': return sub.kz_name;
                                    case 'cn': return sub.ch_name;
                                    default: return sub.name;
                                }
                            };
                            
                            localStorage.setItem("selectedCategoryName", getCategoryName(foundCategory, currentLang));
                            localStorage.setItem("selectedSubcategoryName", getSubcategoryName(foundSubcategory, currentLang));
                            localStorage.setItem("selectedCategoryId", foundCategory.id);
                            localStorage.setItem("selectedSubcategoryId", foundSubcategory.id);
                            
                            // Trigger storage event for UI update
                            window.dispatchEvent(new Event('localStorageUpdated'));
                        }
                    }
                } catch (categoryError) {
                    console.error("Error fetching categories:", categoryError);
                }
            } else {
                // Fallback to old behavior if IDs are not available
                try {
                    const categories = await getCategories();
                    const foundCategory = categories.find(cat => 
                        cat.name.toLowerCase().includes(value.toLowerCase()) ||
                        value.toLowerCase().includes(cat.name.toLowerCase())
                    );
                    
                    if (foundCategory) {
                        setSelectedCategory(foundCategory.id);
                        saveToLocalStorage("selectedCategory", foundCategory.id);
                    }
                } catch (categoryError) {
                    console.error("Error fetching categories:", categoryError);
                }
            }
        } else if (type === 'keyword' && value) {
            // Handle keyword for announcement name
            setAnnouncementName(value);
            localStorage.setItem("announcementName", value);
        }
    };

    const handleAIGenerate = async (title) => {
        if (title && title.trim().length >= 3) {
            // Prevent multiple simultaneous requests
            if (isProcessing) {
                console.log('AI request already in progress, skipping');
                return;
            }
            
            console.log('Making AI request for:', title.trim());
            isProcessing = true;
            
            try {
                await fetchAISuggestions(title.trim());
            } catch (error) {
                console.error('AI request failed:', error);
            } finally {
                isProcessing = false;
            }
        }
    };

    const handleUpdateSuggestions = async (title) => {
        if (title && title.trim().length >= 3) {
            // Reset processing flag to allow new request
            isProcessing = false;
            
            try {
                await fetchAISuggestions(title);
            } catch (error) {
                console.error('Update AI request failed:', error);
            }
        }
    };

    const cleanup = () => {
        if (aiTimeout) {
            clearTimeout(aiTimeout);
            aiTimeout = null;
        }
        isProcessing = false;
    };

    return {
        handleImagesChange,
        handleVideoLinkChange,
        handleAnnouncementInputChange,
        handleCustomTagsChange,
        handleSelectCategory,
        handleDescriptionChange,
        handleVolumeChange,
        handleSelectLocation,
        handleSuggestionClick,
        handleAIGenerate,
        handleUpdateSuggestions,
        cleanup
    };
}